import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { CTABand } from "../../components/site/CTABand";
import { Reveal } from "../../components/motion/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  { slug: "request-quote", label: "Request a Quote", messageLabel: "What do you need a quote for?", placeholder: "Tell us the products or systems you need, quantities, and your site location…" },
  { slug: "amc-inquiry", label: "AMC Inquiry", messageLabel: "What should the AMC cover?", placeholder: "Existing systems, age of equipment, number of devices/cameras, current issues…" },
  { slug: "site-survey", label: "Site Survey Request", messageLabel: "Tell us about your site", placeholder: "Site type (office/apartment/factory), address or area, and what you want assessed…" },
  { slug: "dealership", label: "Dealership Inquiry", messageLabel: "Your business & interest", placeholder: "Your business profile, location, brands you deal in, and what partnership you are looking for…" },
  { slug: "technical-support", label: "Technical Support", messageLabel: "Describe the issue", placeholder: "Which system is affected, what is happening, since when, and any error messages…" },
];

const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

export default function Inquiries() {
  const [type, setType] = useState("request-quote");
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const current = INQUIRY_TYPES.find((t) => t.slug === type);

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, { type, ...form });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again or call us directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="inquiries-page">
      <SiteHeader />
      <PageHero
        crumb="Inquiries"
        eyebrow="Inquiries"
        title="Tell us what you need."
        titleAccent="We respond within a day."
        description="Quotes, AMC, site surveys, dealership partnerships or technical support — pick the right form and it lands directly with the right team."
      />
      <section className="py-16 sm:py-24">
        <div className="nx-wrap max-w-4xl">
          {done ? (
            <Reveal>
              <div className="border border-nx-gold/30 bg-nx-gold/[0.05] p-10 sm:p-14 text-center" data-testid="inquiry-success">
                <CheckCircle2 size={44} className="text-nx-goldlight mx-auto mb-6" strokeWidth={1.5} />
                <h2 className="font-marcellus text-2xl sm:text-3xl text-white mb-4">Inquiry received.</h2>
                <p className="text-sm text-slate-400 leading-relaxed max-w-md mx-auto">Thank you, {form.name.split(" ")[0]}. Our {type === "technical-support" ? "support" : "sales"} team will get back to you within one business day (Mon–Sat, 9:30 AM – 5:30 PM IST).</p>
                <button onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", company: "", message: "" }); }} className="mt-8 text-xs tracking-[0.18em] uppercase text-nx-goldlight hover:text-white transition-colors" data-testid="inquiry-another">
                  Submit another inquiry →
                </button>
              </div>
            </Reveal>
          ) : (
            <>
              <Reveal>
                <div className="flex flex-wrap gap-2.5 mb-10" data-testid="inquiry-type-picker">
                  {INQUIRY_TYPES.map((t) => (
                    <button
                      key={t.slug}
                      onClick={() => setType(t.slug)}
                      className={`text-[11px] tracking-[0.14em] uppercase px-5 py-3 border transition-colors ${type === t.slug ? "border-nx-gold/70 bg-nx-gold/[0.08] text-nx-goldlight" : "border-white/15 text-slate-300 hover:border-nx-gold/50 hover:text-white"}`}
                      data-testid={`inquiry-type-${t.slug}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-7 sm:p-10 flex flex-col gap-5" data-testid="inquiry-form">
                  <p className="text-xs tracking-[0.2em] uppercase text-slate-500">{current.label} — goes straight to our {type === "technical-support" ? "technical support" : "sales"} team</p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <input required minLength={1} maxLength={120} placeholder="Full Name *" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inquiry-name-input" />
                    <input required type="tel" maxLength={16} placeholder="Phone Number *" className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d+\s-]/g, "") })} data-testid="inquiry-phone-input" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <input required type="email" maxLength={160} placeholder="Email Address *" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inquiry-email-input" />
                    <input maxLength={160} placeholder="Company Name" className={input} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="inquiry-company-input" />
                  </div>
                  <div>
                    <label className="block text-[11px] tracking-[0.18em] uppercase text-slate-500 mb-2.5">{current.messageLabel} *</label>
                    <textarea required minLength={1} maxLength={4000} rows={5} placeholder={current.placeholder} className={`${input} resize-none`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="inquiry-message-input" />
                  </div>
                  <button type="submit" disabled={busy} className="group inline-flex items-center justify-center gap-3 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-sm tracking-wide px-9 py-4 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 sm:self-start" data-testid="inquiry-submit">
                    {busy ? "Sending…" : "Submit Inquiry"} <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </form>
              </Reveal>
            </>
          )}
        </div>
      </section>
      <CTABand />
      <SiteFooter />
    </div>
  );
}
